/**
 * Phone matching for ingestion and OpenPhone webhooks.
 * Hub historically stores free-form `contacts.phone`; matching tolerates multiple representations.
 * New ingestion prefers canonical US E.164 (`+1` + 10 digits) via normalizePhoneForInboundHub.
 */

import type { SupabaseClient } from "@wtc/supabase";
import { logger } from "../../logger";

/** NANP-style US national number without country code (NXX-NXX-XXXX, N≠0/1 in positions checked here). */
function isLikelyUsNanpTenDigit(digits10: string): boolean {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits10);
}

/**
 * Single canonical form to persist when the CRM receives phones from external systems.
 *
 * - **US**: 10-digit national or 11 digits starting with `1` → `+1` + 10-digit national (always includes country code).
 * - **Explicit international**: input trimmed value starts with `+` → `+` plus digits only (e.g. `+44…`).
 * - **Otherwise**: returns trimmed original string so we do not guess a country code.
 */
export function normalizePhoneForInboundHub(input: string | null | undefined): string | undefined {
  if (input == null || typeof input !== "string") return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return undefined;

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1${digits.slice(1)}`;
  }
  // Avoid treating UK/local leading-zero numbers as US when formatted without a country code.
  if (
    digits.length === 10 &&
    !trimmed.startsWith("0") &&
    isLikelyUsNanpTenDigit(digits)
  ) {
    return `+1${digits}`;
  }
  if (trimmed.startsWith("+")) {
    return `+${digits}`;
  }

  return trimmed;
}

/**
 * True when a string is a plausibly-dialable phone number — i.e. its digit count
 * falls within the E.164 range (min significant national ~7, absolute max 15,
 * inclusive of country code). Guards against ingestion garbage such as
 * concatenated numbers (`+1309696112119012833578`, 22 digits) being treated as a
 * real number for matching, merging, or display.
 *
 * Deliberately loose on FORMAT (accepts any punctuation) and strict only on
 * LENGTH — the point is to reject impossible numbers, not to police formatting.
 */
export function isPlausiblePhoneNumber(input: string | null | undefined): boolean {
  if (input == null || typeof input !== "string") return false;
  const digits = input.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

/**
 * Build a Slack-/RFC-3966-safe `tel:` href from any phone representation, or
 * `undefined` when there is nothing dialable.
 *
 * Why this exists: `normalizePhoneForInboundHub` may return its raw trimmed
 * input (parens, spaces, extensions, or even a non-numeric label like
 * "Wireless Caller") when the value doesn't match a clean US/international
 * shape. Interpolating that straight into `tel:${value}` yields a `url` with
 * spaces/parens, which Slack rejects with `invalid_url` (`invalid_blocks`) —
 * and one bad button fails the ENTIRE `chat.postMessage`. `tel:` URIs may carry
 * only `+` and digits, so we strip to exactly that and require enough digits to
 * be a real number; otherwise the caller should omit the button.
 */
export function telHrefForPhone(input: string | null | undefined): string | undefined {
  const normalized = normalizePhoneForInboundHub(input);
  if (!normalized) return undefined;

  const hasPlus = normalized.trim().startsWith("+");
  const digits = normalized.replace(/\D/g, "");
  // Need a plausibly-dialable number; guards against extensions/labels collapsing to too-few digits.
  if (digits.length < 7) return undefined;

  return `tel:${hasPlus ? "+" : ""}${digits}`;
}

/** US NANP: strip non-digits, collapse leading country code 1 to 10-digit national when applicable. */
export function phoneDigitsForComparison(input: string | null | undefined): string | null {
  if (!input || typeof input !== "string") return null;
  const digits = input.replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  if (digits.length === 10) return digits;
  return digits;
}

function addUsNationalVariants(set: Set<string>, national10: string): void {
  if (national10.length !== 10) return;
  const a = national10.slice(0, 3);
  const b = national10.slice(3, 6);
  const c = national10.slice(6);
  set.add(national10);
  set.add(`1${national10}`);
  set.add(`+1${national10}`);
  set.add(`+1 ${national10}`);
  set.add(`(${a}) ${b}-${c}`);
  set.add(`${a}-${b}-${c}`);
  set.add(`${a}.${b}.${c}`);
  set.add(`${a} ${b} ${c}`);
  set.add(`+1 (${a}) ${b}-${c}`);
  set.add(`1-${a}-${b}-${c}`);
  set.add(`+1-${a}-${b}-${c}`);
}

/**
 * Strings to try with `.in("phone", variants)` — typical CRM / OpenPhone representations.
 */
export function phoneExactMatchVariants(raw: string): string[] {
  const trimmed = raw.trim();
  const variants = new Set<string>();
  if (trimmed.length > 0) variants.add(trimmed);

  const digitsOnly = raw.replace(/\D/g, "");
  if (digitsOnly.length > 0) variants.add(digitsOnly);

  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    addUsNationalVariants(variants, digitsOnly.slice(1));
    variants.add(`+${digitsOnly}`);
  } else if (digitsOnly.length === 10) {
    addUsNationalVariants(variants, digitsOnly);
  }

  return [...variants].filter((v) => v.length > 0);
}

export interface ContactRowPhoneLookup {
  id: string;
  name: string;
  email: string;
  email_aliases: string[] | null;
  phone: string | null;
  phone_aliases?: string[] | null;
  company_id: string | null;
  company_name: string | null;
  status: string | null;
}

/**
 * Resolve all contact rows matching a phone (primary or phone_aliases).
 * Used by OpenPhone webhooks when a shared company line maps to multiple people.
 */
export async function findContactRowsByPhoneMatch(
  supabase: SupabaseClient,
  rawPhone: string
): Promise<ContactRowPhoneLookup[]> {
  const { data: byDigits, error: digitsErr } = await supabase.rpc(
    "find_contacts_by_phone_digits",
    { p_raw_phone: rawPhone }
  );

  if (digitsErr) {
    logger.error("failed to query contacts by phone digits RPC (plural)", digitsErr, {
      op: "findContactRowsByPhoneMatch",
    });
  } else if (byDigits && byDigits.length > 0) {
    return byDigits;
  }

  const single = await findContactRowByPhoneMatchFallback(supabase, rawPhone);
  return single ? [single] : [];
}

/**
 * Resolve an existing contact row when `contacts.phone` matches common formatting variants
 * or shares the same digit-normalized key (last resort ilike on digits substring).
 */
export async function findContactRowByPhoneMatch(
  supabase: SupabaseClient,
  rawPhone: string
): Promise<ContactRowPhoneLookup | null> {
  const { data: byDigits, error: digitsErr } = await supabase.rpc(
    "find_contact_by_phone_digits",
    { p_raw_phone: rawPhone }
  );

  if (digitsErr) {
    logger.error("failed to query contacts by phone digits RPC", digitsErr, {
      op: "findContactRowByPhoneMatch",
    });
  } else {
    const rpcRow = byDigits?.[0];
    if (rpcRow) return rpcRow;
  }

  return findContactRowByPhoneMatchFallback(supabase, rawPhone);
}

async function findContactRowByPhoneMatchFallback(
  supabase: SupabaseClient,
  rawPhone: string
): Promise<ContactRowPhoneLookup | null> {
  const variants = phoneExactMatchVariants(rawPhone);
  if (variants.length === 0) return null;

  const { data: byVariants, error: variantErr } = await supabase
    .from("contacts")
    .select("id, name, email, email_aliases, phone, company_id, company_name, status")
    .in("phone", variants)
    .limit(1);

  if (variantErr) {
    logger.error("failed to query contacts by phone variants", variantErr, {
      op: "findContactRowByPhoneMatch",
    });
  }
  const firstVariant = byVariants?.[0];
  if (firstVariant) return firstVariant;

  const key = phoneDigitsForComparison(rawPhone);
  if (!key || key.length < 7) return null;

  const { data: candidates, error: looseErr } = await supabase
    .from("contacts")
    .select("id, name, email, email_aliases, phone, company_id, company_name, status")
    .not("phone", "is", null)
    .ilike("phone", `%${key}%`)
    .limit(25);

  if (looseErr) {
    logger.error("failed in fallback phone ilike query", looseErr, {
      op: "findContactRowByPhoneMatch",
    });
    return null;
  }

  const row = (candidates ?? []).find((c) => phoneDigitsForComparison(c.phone) === key);
  return row ?? null;
}
